import { stepsRegistry } from "@/features/multistep/registry";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { StepDefinition } from "@/features/multistep/registry/types";
import { useFormStepper } from "@/hooks/useFormStepper";
import { useMultiStepFormData } from "@/hooks/useMultiStepFormData";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";

import axios from "axios";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GlobalFormSchema } from "@/schemas/multistep/global-form.schema";
import { getStepFields } from "@/components/form-steps/helpers";
import { requestService } from "@/services";

export function MultiStepForm() {
  const [isStepValid, setIsStepValid] = useState(false);
  const form = useForm({
    resolver: zodResolver(GlobalFormSchema),
    defaultValues: {},
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    control,
    reset,
    formState: { errors },
  } = form;

  const tipoDocumento = useWatch({
    control,
    name: "informacionBasica.tipoDocumento",
  });

  // 🔹 Todos los steps registrados
  const steps: StepDefinition[] = Object.values(stepsRegistry);

  // useEffect(() => {
  //   if (Object.keys(errors).length) {
  //     console.log("Errores actuales en el formulario desde el header:", errors);
  //   } else {
  //     console.log("El objeto errors está vacío");
  //   }
  // }, [JSON.stringify(errors)]);

  const { saveStepData } = useMultiStepFormData();

  // 🔹 Hook que maneja la navegación
  const {
    currentStepId,
    goNext,
    goPrev,
    isFirstStep,
    isLastStep,
    currentStepIndex,
    applicableSteps,
    reset: resetSteps,
  } = useFormStepper(steps, tipoDocumento);

  const currentStep = useMemo(
    () => stepsRegistry[currentStepId] ?? stepsRegistry["generalidades"],
    [currentStepId]
  );

  const values = useWatch({ control, name: currentStepId });

  useEffect(() => {
    if (!currentStep?.schema) return;

    const stepFields: string[] = getStepFields(
      currentStepId,
      currentStep.schema,
      form
    );

    // console.log(`stepFields  ${currentStepId}:>> `, stepFields);
    if (!stepFields.length) {
      setIsStepValid(false);
      return;
    }

    // Nos suscribimos a los cambios de los valores de los campos de este step
    const subscription = form.watch(async (_, { name }) => {
      // Solo revalida si el cambio corresponde a los campos de este step
      if (name && stepFields.includes(name)) {
        const isValid = await form.trigger(stepFields as any);
        setIsStepValid(isValid);
      }
    });

    // Validamos al entrar al step
    form.trigger(stepFields as any).then(setIsStepValid);

    return () => subscription.unsubscribe();
  }, [currentStepId, currentStep?.schema, form, values]);

  if (!currentStep) return <div>No hay step</div>;

  const StepComponent = currentStep.Component;

  const handleNext = async () => {
    const stepFields = getStepFields(currentStepId, currentStep.schema, form);
    const isValid = await form.trigger(stepFields as any);
    if (!isValid) return;

    const stepValues = form.getValues(currentStepId as any);
    saveStepData(currentStep.id, stepValues);
    goNext();
  };

  const downloadPdf = async (id: string) => {
    const response = await requestService.downloadPdf(id);
    const blob = new Blob([response.data], { type: response.data.type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sarlaft_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleFinalSubmit = async (values: any) => {
    const isValid = await form.trigger();
    if (!isValid) return;

    saveStepData(currentStep.id, values);

    // 🔹 Validación global
    const parsed = GlobalFormSchema.safeParse(values);
    if (!parsed.success) {
      return;
    }

    try {
      await requestService.save(parsed.data).then(async () => {
        await downloadPdf(parsed.data.informacionBasica.numeroDocumento);
        reset();
        resetSteps();
        toast.success("Formulario enviado");
      });
    } catch (error: any) {
      console.log("error :>> ", error);
      if (axios.isAxiosError(error)) {
        if (error.status === 400) {
          toast.error("Valide los datos ingresados");
          form.trigger();
          return;
        }
        if (error.status === 409) {
          toast.error(error.response?.data.message);
          return;
        }

        if (error.code === "ERR_NETWORK") {
          toast.error("Contacte con el administrador del sistema");
          return;
        }
      }
    }
  };

  const progressValue = ((currentStepIndex + 1) / applicableSteps.length) * 100;
  const stepErrors = errors?.[currentStepId] as Record<string, any> | undefined;

  return (
    <>
      <FormProvider {...form}>
        <form
          key={currentStep.id}
          onSubmit={form.handleSubmit(handleFinalSubmit)}
          className="flex justify-center w-full px-4"
        >
          <Card className="w-full max-w-md sm:max-w-xl md:max-w-3xl lg:max-w-4xl shadow-lg">
            {/* Header con título dinámico */}
            <CardHeader>
              {/* 🔹 Step indicator */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="text-lg font-bold">
                  {currentStep.title}
                </CardTitle>
                <span className="text-sm text-gray-500">
                  Paso {currentStepIndex + 1} de {applicableSteps.length}
                </span>
              </div>
              {/* 🔹 Progress bar */}
              <Progress value={progressValue} className="mt-4 h-2" />
            </CardHeader>

            {/* Contenido del step */}
            <CardContent>
              <StepComponent key={currentStep.id} stepId={currentStep.id} />
              {/* {isSubmitted && !isValid && (
                <div className="bg-red-50 border border-red-300 p-3 rounded mb-4">
                  <p className="text-red-600 font-semibold">
                    Por favor corrige los siguientes errores:
                  </p>
                  <ul className="list-disc pl-5 text-red-500">
                    {Object.entries(errors).map(([key, value]) => (
                      <li key={key}>{(value as any).message}</li>
                    ))}
                  </ul>
                </div>
              )} */}
            </CardContent>

            {/* Footer con botones de navegación */}
            <CardFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button
                type="button"
                onClick={goPrev}
                disabled={isFirstStep}
                className="w-full sm:w-auto"
              >
                Anterior
              </Button>
              <Button
                type={isLastStep ? "submit" : "button"}
                onClick={!isLastStep ? handleNext : undefined}
                className="w-full sm:w-auto"
                disabled={
                  !isStepValid ||
                  (stepErrors && Object.keys(stepErrors).length > 0)
                }
              >
                {isLastStep ? "Finalizar" : "Siguiente"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </FormProvider>
    </>
  );
}
